<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'dashboard/composables/store';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';
import ReportHeader from './components/ReportHeader.vue';
import ConversationHeatmapContainer from './components/heatmaps/ConversationHeatmapContainer.vue';
import ResolutionHeatmapContainer from './components/heatmaps/ResolutionHeatmapContainer.vue';
import AgentLiveReportContainer from './components/AgentLiveReportContainer.vue';
import TeamLiveReportContainer from './components/TeamLiveReportContainer.vue';
import StatsLiveReportsContainer from './components/StatsLiveReportsContainer.vue';
import AskLumera from './components/AskLumera.vue';
import LumeraInsightDashboard from './components/LumeraInsightDashboard.vue';
import ReportsAPI from 'dashboard/api/reports';

const store = useStore();
const { t, locale } = useI18n();
const analysis = ref(null);
const savedReports = ref([]);
const schedules = ref([]);
const savedReportToOpen = ref(null);
const isLoadingSavedReports = ref(false);
const analyticsQuota = ref(null);
const reportMode = ref('analytical');
const copy = computed(() => ({
  basicView: t('OVERVIEW_REPORTS.ANALYTICS.BASIC_VIEW'),
  analyticalView: t('OVERVIEW_REPORTS.ANALYTICS.ANALYTICAL_VIEW'),
  savedReports: t('OVERVIEW_REPORTS.ANALYTICS.SAVED_REPORTS'),
  noSavedReports: t('OVERVIEW_REPORTS.ANALYTICS.NO_SAVED_REPORTS'),
  deleteReport: t('OVERVIEW_REPORTS.ANALYTICS.DELETE_REPORT'),
  schedules: t('OVERVIEW_REPORTS.ANALYTICS.SCHEDULES'),
  noSchedules: t('OVERVIEW_REPORTS.ANALYTICS.NO_SCHEDULES'),
  pause: t('OVERVIEW_REPORTS.ANALYTICS.PAUSE'),
  resume: t('OVERVIEW_REPORTS.ANALYTICS.RESUME'),
  removeSchedule: t('OVERVIEW_REPORTS.ANALYTICS.REMOVE_SCHEDULE'),
  quotaTitle: t('OVERVIEW_REPORTS.ANALYTICS.QUOTA_TITLE'),
  quotaRemaining: (remaining, max) =>
    t('OVERVIEW_REPORTS.ANALYTICS.QUOTA_REMAINING', { remaining, max }),
  quotaReset: label => t('OVERVIEW_REPORTS.ANALYTICS.QUOTA_RESET', { label }),
}));
const hasAdvancedReports = computed(() => {
  const account = store.getters.getCurrentAccount || {};
  const modules = account.enabled_modules || {};
  const features = account.features || {};
  const key = FEATURE_FLAGS.REPORTS;

  return (
    modules[key] === true ||
    modules[key] === 'true' ||
    (Array.isArray(features)
      ? features.includes(key)
      : features[key] === true || features[key] === 'true')
  );
});
const isAnalytical = computed(
  () => hasAdvancedReports.value && reportMode.value === 'analytical'
);
const analyticsQuotaLabel = computed(() => {
  const resetAt = analyticsQuota.value?.reset_at;
  if (!resetAt) return '';

  const dateLocale = locale.value === 'pt_BR' ? 'pt-BR' : locale.value;
  return new Intl.DateTimeFormat(dateLocale, {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'America/Sao_Paulo',
  }).format(new Date(resetAt));
});

watch(
  () => store.getters.getCurrentAccount?.id,
  accountId => {
    if (!accountId || typeof window === 'undefined') return;

    const savedMode = window.localStorage.getItem(
      `reports-view-mode-${accountId}`
    );
    reportMode.value = savedMode === 'basic' ? 'basic' : 'analytical';
  },
  { immediate: true }
);

const selectReportMode = mode => {
  reportMode.value = mode;
  const accountId = store.getters.getCurrentAccount?.id;
  if (accountId && typeof window !== 'undefined') {
    window.localStorage.setItem(`reports-view-mode-${accountId}`, mode);
  }
};

const loadSavedReports = async () => {
  if (!hasAdvancedReports.value) return;

  isLoadingSavedReports.value = true;
  try {
    const { data } = await ReportsAPI.getSavedLumeraReports();
    savedReports.value = data;
    const scheduleResponse = await ReportsAPI.getLumeraReportSchedules();
    schedules.value = scheduleResponse.data;
  } finally {
    isLoadingSavedReports.value = false;
  }
};

const loadAnalyticsQuota = async () => {
  if (!hasAdvancedReports.value) return;

  try {
    const { data } = await ReportsAPI.getLumeraAnalyticsReportQuota();
    analyticsQuota.value = data;
  } catch {
    analyticsQuota.value = null;
  }
};

const toggleSchedule = async schedule => {
  await ReportsAPI.updateLumeraReportSchedule(schedule.id, {
    active: !schedule.active,
  });
  await loadSavedReports();
};

const deleteSchedule = async schedule => {
  await ReportsAPI.deleteLumeraReportSchedule(schedule.id);
  await loadSavedReports();
};

const openSavedReport = report => {
  savedReportToOpen.value = { ...report, openedAt: Date.now() };
};

const deleteSavedReport = async report => {
  await ReportsAPI.deleteLumeraReport(report.id);
  await loadSavedReports();
};

const openDrilldown = question => {
  savedReportToOpen.value = {
    question,
    filters: analysis.value?.query?.filters || {},
    openedAt: Date.now(),
  };
};

const receiveAnalysis = value => {
  analysis.value = value;
  loadAnalyticsQuota();
};

onMounted(() => {
  loadSavedReports();
  loadAnalyticsQuota();
});
</script>

<template>
  <ReportHeader :header-title="$t('OVERVIEW_REPORTS.HEADER')" />
  <div class="flex flex-col gap-4 pb-6">
    <div
      v-if="hasAdvancedReports"
      class="flex flex-wrap items-center justify-between gap-3"
    >
      <div class="flex w-fit rounded-lg bg-n-alpha-2 p-1">
        <button
          type="button"
          class="rounded-md px-3 py-2 text-sm font-medium transition-colors"
          :class="
            reportMode === 'basic'
              ? 'bg-n-surface-1 text-n-slate-12 shadow-sm'
              : 'text-n-slate-11'
          "
          @click="selectReportMode('basic')"
        >
          {{ copy.basicView }}
        </button>
        <button
          type="button"
          class="rounded-md px-3 py-2 text-sm font-medium transition-colors"
          :class="
            reportMode === 'analytical'
              ? 'bg-n-surface-1 text-n-slate-12 shadow-sm'
              : 'text-n-slate-11'
          "
          @click="selectReportMode('analytical')"
        >
          {{ copy.analyticalView }}
        </button>
      </div>
      <div
        v-if="isAnalytical && analyticsQuota"
        class="min-w-[180px] rounded-lg border border-fuchsia-500/30 bg-fuchsia-500/10 px-3 py-2 text-right"
      >
        <p class="m-0 text-[11px] font-medium text-n-slate-10">
          {{ copy.quotaTitle }}
        </p>
        <p class="m-0 text-sm font-bold text-n-slate-12">
          {{
            copy.quotaRemaining(analyticsQuota.remaining, analyticsQuota.max)
          }}
        </p>
        <p class="m-0 text-[10px] text-n-slate-10">
          {{ copy.quotaReset(analyticsQuotaLabel) }}
        </p>
      </div>
    </div>
    <template v-if="isAnalytical">
      <section class="rounded-xl border border-n-strong bg-n-alpha-2 p-4">
        <div class="flex items-center justify-between gap-3">
          <h2 class="text-sm font-semibold text-n-slate-12">
            {{ copy.savedReports }}
          </h2>
        </div>
        <p
          v-if="!isLoadingSavedReports && !savedReports.length"
          class="mb-0 mt-2 text-xs text-n-slate-10"
        >
          {{ copy.noSavedReports }}
        </p>
        <div v-else class="mt-3 flex flex-wrap gap-2">
          <div
            v-for="report in savedReports"
            :key="report.id"
            class="flex overflow-hidden rounded-md border border-n-strong"
          >
            <button
              type="button"
              class="px-3 py-2 text-left text-xs text-n-slate-12 hover:bg-n-surface-1"
              @click="openSavedReport(report)"
            >
              {{ report.name }}
            </button>
            <button
              type="button"
              class="border-s border-n-strong px-2 text-n-slate-10 hover:text-red-500"
              :aria-label="copy.deleteReport"
              @click="deleteSavedReport(report)"
            >
              <i class="i-lucide-trash-2" />
            </button>
          </div>
        </div>
      </section>
      <section class="rounded-xl border border-n-strong bg-n-alpha-2 p-4">
        <h2 class="text-sm font-semibold text-n-slate-12">
          {{ copy.schedules }}
        </h2>
        <p v-if="!schedules.length" class="mb-0 mt-2 text-xs text-n-slate-10">
          {{ copy.noSchedules }}
        </p>
        <div v-else class="mt-3 flex flex-col gap-2">
          <div
            v-for="schedule in schedules"
            :key="schedule.id"
            class="flex flex-wrap items-center justify-between gap-2 rounded-lg bg-n-surface-1 p-3 text-xs"
          >
            <span class="text-n-slate-12">
              <strong>{{ schedule.report_name }}</strong>
              <span aria-hidden="true">{{
                $t('COMMON.BULLET_SEPARATOR')
              }}</span>
              {{ schedule.frequency }}
              <span aria-hidden="true">{{
                $t('COMMON.BULLET_SEPARATOR')
              }}</span>
              {{ schedule.recipient_email }}
            </span>
            <span class="flex gap-2">
              <button
                type="button"
                class="text-blue-500"
                @click="toggleSchedule(schedule)"
              >
                {{ schedule.active ? copy.pause : copy.resume }}
              </button>
              <button
                type="button"
                class="text-red-500"
                @click="deleteSchedule(schedule)"
              >
                {{ copy.removeSchedule }}
              </button>
            </span>
          </div>
        </div>
      </section>
      <AskLumera
        :saved-report="savedReportToOpen"
        @analysis="receiveAnalysis"
        @loading="analysis = null"
      />
      <LumeraInsightDashboard
        v-if="analysis"
        :analysis="analysis"
        @saved="loadSavedReports"
        @drill="openDrilldown"
      />
    </template>
    <template v-else>
      <StatsLiveReportsContainer />
      <ConversationHeatmapContainer />
      <ResolutionHeatmapContainer />
      <AgentLiveReportContainer />
      <TeamLiveReportContainer />
    </template>
  </div>
</template>
